import React, { useEffect, useState } from 'react';
import { Row, Col, Form, Button, Alert, Modal } from 'react-bootstrap';
import axios from 'axios';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useNotification, NotificationMessages } from '../hooks/useNotification';

const EXTENSION_AGENDAS = [
  'BatStateU Inclusive Social Innovation for Regional Growth (BISIG) Program',
  'Livelihood and other Entrepreneurship related on Agri-Fisheries (LEAF)',
  'Environment and Natural Resources Conservation, Protection and Rehabilitation Program',
  'Smart Analytics and Engineering Innovation',
  'Adopt-a Municipality/Barangay/School/Social Development Thru BIDANI Implementation',
  'Community Outreach',
  'Technical- Vocational Education and Training (TVET) Program',
  'Technology Transfer and Adoption/Utilization Program',
  'Technical Assistance and Advisory Services Program',
  "Parents' Empowerment through Social Development (PESODEV)",
  'Gender and Development',
  'Disaster Risk Reduction and Management and Disaster Preparedness and Response/Climate Change Adaptation (DRRM and DPR/CCA)'
];

const SDG_GOALS = [
  'No Poverty',
  'Zero Hunger',
  'Good Health and Well-Being',
  'Quality Education',
  'Gender Equality',
  'Clean Water and Sanitation',
  'Affordable and Clean Energy',
  'Decent Work and Economic Growth',
  'Industry, Innovation and Infrastructure',
  'Reduced Inequalities',
  'Sustainable Cities and Communities',
  'Climate Action',
  'Life Below Water',
  'Life on Land',
  'Peace, Justice and Strong Institutions',
  'Partnerships for the Goals'
];

const ProjectEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showSuccess } = useNotification();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [project, setProject] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [fundSourceOther, setFundSourceOther] = useState('');
  
  // Check if user can edit dates and status (Admin only for status, OR coordinators for rejected projects)
  const isElevatedRole = user && ['Extension Head', 'GAD', 'Vice Chancellor for Administration and Finance', 'Vice Chancellor for Research, Development and Extension Services', 'Chancellor', 'Admin'].includes(user.role);
  const canEditStatus = user && user.role === 'Admin'; // Only Admin can edit status
  
  // Calculate canEditDates after project is loaded
  const canEditDates = isElevatedRole || (
    user && user.role === 'Extension Coordinator' && 
    project && project.coordinator_id === user.user_id
  );
  const [form, setForm] = useState({
    request_type: '',
    event_type: '',
    title: '',
    location: '',
    start_date: '',
    end_date: '',
    start_time: '',
    end_time: '',
    extension_agenda: '',
    sdg_goals: [],
    offices_involved: '',
    programs_involved: '',
    project_leader: null, // Single user object
    project_leader_task: '',
    assistant_project_leader: null, // Single user object
    assistant_project_leader_task: '',
    coordinator: null, // Single user object
    coordinator_task: '',
    partner_agencies: '',
    beneficiaries: '',
    total_cost: '',
    fund_source: '',
    rationale: '',
    objectives: '',
    expected_output: '',
    strategies_methods: '',
    financial_plan_details: '',
    functional_relationships: '',
    monitoring_evaluation: '',
    sustainability_plan: ''
  });

  // Financial Plan rows (editable table)
  const [financialPlanRows, setFinancialPlanRows] = useState([
    { description: '', quantity: '', unit: '', price: '', total: '' },
    { description: '', quantity: '', unit: '', price: '', total: '' },
    { description: '', quantity: '', unit: '', price: '', total: '' },
    { description: '', quantity: '', unit: '', price: '', total: '' }
  ]);

  const addFinancialPlanRow = () => {
    setFinancialPlanRows(prev => {
      const next = [...prev, { description: '', quantity: '', unit: '', price: '', total: '' }];
      setForm(f => ({ ...f, financial_plan_details: JSON.stringify(next) }));
      return next;
    });
  };

  const removeFinancialPlanRow = () => {
    setFinancialPlanRows(prev => {
      if (prev.length <= 4) return prev; // keep minimum 4
      const next = prev.slice(0, prev.length - 1);
      setForm(f => ({ ...f, financial_plan_details: JSON.stringify(next) }));
      return next;
    });
  };

  useEffect(() => {
    // Try to parse existing financial_plan_details into table rows
    if (form.financial_plan_details) {
      try {
        const parsed = JSON.parse(form.financial_plan_details);
        if (Array.isArray(parsed) && parsed.length) {
          setFinancialPlanRows(parsed.map(r => ({ description: r.description || '', quantity: r.quantity || '', unit: r.unit || '', price: r.price || '', total: r.total || '' })));
        }
      } catch {}
    }
  }, [form.financial_plan_details]);

  const updateFinancialRow = (index, key, value) => {
    setFinancialPlanRows(prev => {
      const sanitizeNumber = (v) => {
        if (v === null || v === undefined) return '';
        const cleaned = String(v).replace(/[^\d.]/g, '');
        const parts = cleaned.split('.');
        if (parts.length > 2) {
          return parts[0] + '.' + parts.slice(1).join('');
        }
        return cleaned;
      };
      const next = prev.map((r, i) => {
        if (i !== index) return r;
        if (key === 'price' || key === 'total') {
          return { ...r, [key]: sanitizeNumber(value) };
        }
        return { ...r, [key]: value };
      });
      setForm(f => ({ ...f, financial_plan_details: JSON.stringify(next) }));
      return next;
    });
  };

  const getFinancialPlanGrandTotal = () => {
    return financialPlanRows.reduce((sum, r) => sum + (parseFloat(r.total) || 0), 0);
  };

  // Monitoring and Evaluation table data
  const [meTableData, setMeTableData] = useState({
    impact: { objectives: '', performance_indicators: '', baseline_data: '', performance_target: '', data_source: '', collection_method: '', frequency: '', responsible: '' },
    outcome: { objectives: '', performance_indicators: '', baseline_data: '', performance_target: '', data_source: '', collection_method: '', frequency: '', responsible: '' },
    output: { objectives: '', performance_indicators: '', baseline_data: '', performance_target: '', data_source: '', collection_method: '', frequency: '', responsible: '' },
    activities: { objectives: '', performance_indicators: '', baseline_data: '', performance_target: '', data_source: '', collection_method: '', frequency: '', responsible: '' },
    input: { objectives: '', performance_indicators: '', baseline_data: '', performance_target: '', data_source: '', collection_method: '', frequency: '', responsible: '' }
  });
  // M&E rows (free-text table with fixed columns)
  const makeEmptyMeRow = () => ({ objectives: '', performance_indicators: '', baseline_data: '', performance_target: '', data_source: '', collection_method: '', frequency: '', responsible: '' });
  const [meRows, setMeRows] = useState([makeEmptyMeRow(), makeEmptyMeRow(), makeEmptyMeRow(), makeEmptyMeRow(), makeEmptyMeRow()]);
  const addMeRow = () => setMeRows(prev => [...prev, makeEmptyMeRow()]);
  const removeMeRow = () => setMeRows(prev => prev.length > 5 ? prev.slice(0, prev.length - 1) : prev);
  const [participantsOptions, setParticipantsOptions] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [roleFilter] = useState('All');
  const [participantSearchTerm] = useState('');
  
  // User search states for the user fields
  const [userSearchOptions, setUserSearchOptions] = useState([]);
  const [projectLeaderSearch] = useState('');
  // Role filters for each field
  const [projectLeaderRoleFilter] = useState('All');
  
  // Selected users for each field
  const [selectedProjectLeader, setSelectedProjectLeader] = useState(null);
  const [assistantLeaders, setAssistantLeaders] = useState([{ user_id: '', fullname: '', role: '', department_college: '' }]); // array of user objects with default field
  const [assistantTasks, setAssistantTasks] = useState({}); // { [user_id]: task }
  const [coordinators, setCoordinators] = useState([{ user_id: '', fullname: '', role: '', department_college: '' }]); // array of user objects with default field
  const [coordinatorTasks, setCoordinatorTasks] = useState({}); // { [user_id]: task }
  const [projectStaffs, setProjectStaffs] = useState([{ user_id: '', fullname: '', role: '', department_college: '' }]); // array of user objects with default field
  const [projectStaffTasks, setProjectStaffTasks] = useState({}); // { [user_id]: task }
  
  // Function to format number with commas and decimal places
  const formatNumberWithCommas = (value) => {
    // Remove all characters except digits and decimal point
    const cleanValue = value.replace(/[^\d.]/g, '');
    
    // Split by decimal point
    const parts = cleanValue.split('.');
    const integerPart = parts[0];
    const decimalPart = parts[1];
    
    // Add commas to integer part
    const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    
    // If there's a decimal part, limit to 2 decimal places
    if (decimalPart !== undefined) {
      const limitedDecimal = decimalPart.substring(0, 2);
      return formattedInteger + '.' + limitedDecimal;
    }
    
    return formattedInteger;
  };

  // Function to remove commas from number (keep decimal places)
  const removeCommas = (value) => {
    return value.replace(/,/g, '');
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    
    if (name === 'total_cost') {
      // Format total_cost with commas for display
      const formattedValue = formatNumberWithCommas(value);
      setForm((prev) => ({ ...prev, [name]: formattedValue }));
    } else {
    setForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleCheckboxGroup = (name, value) => {
    setForm((prev) => {
      const current = new Set(prev[name]);
      if (current.has(value)) current.delete(value); else current.add(value);
      return { ...prev, [name]: Array.from(current) };
    });
  };

  const handleFundSourceOther = (e) => {
    setFundSourceOther(e.target.value);
  };

  // eslint-disable-next-line no-unused-vars
  const handleMeTableChange = (objectiveType, field, value) => {
    console.log(`handleMeTableChange called: ${objectiveType}.${field} = ${value}`);
    setMeTableData(prev => {
      const newData = {
      ...prev,
      [objectiveType]: {
        ...prev[objectiveType],
        [field]: value
      }
      };
      console.log(`Setting meTableData to:`, newData);
      return newData;
    });
  };

  // Debug useEffect to monitor meTableData changes
  useEffect(() => {
    console.log('meTableData changed:', meTableData);
    console.log('Performance Indicators in state:', {
      impact: meTableData.impact.performance_indicators,
      outcome: meTableData.outcome.performance_indicators,
      output: meTableData.output.performance_indicators,
      activities: meTableData.activities.performance_indicators,
      input: meTableData.input.performance_indicators
    });
  }, [meTableData]);

  const handleSubmitClick = (e) => {
    e.preventDefault();
    setShowConfirmModal(true);
  };

  // Load project data
  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await axios.get(`/projects/${id}`);
        setProject(data);
        
        // Parse existing data
        const parseList = (value) => { 
          try { 
            const v = JSON.parse(value); 
            return Array.isArray(v) ? v : []; 
          } catch { 
            return []; 
          } 
        };

        // Parse monitoring evaluation table
        let meData = {
          impact: { objectives: '', performance_indicators: '', baseline_data: '', performance_target: '', data_source: '', collection_method: '', frequency: '', responsible: '' },
          outcome: { objectives: '', performance_indicators: '', baseline_data: '', performance_target: '', data_source: '', collection_method: '', frequency: '', responsible: '' },
          output: { objectives: '', performance_indicators: '', baseline_data: '', performance_target: '', data_source: '', collection_method: '', frequency: '', responsible: '' },
          activities: { objectives: '', performance_indicators: '', baseline_data: '', performance_target: '', data_source: '', collection_method: '', frequency: '', responsible: '' },
          input: { objectives: '', performance_indicators: '', baseline_data: '', performance_target: '', data_source: '', collection_method: '', frequency: '', responsible: '' }
        };

        
        // Check for monitoring_evaluation_table first, then monitoring_evaluation
        const monitoringData = data.monitoring_evaluation_table || data.monitoring_evaluation;
        console.log('Available monitoring fields:', Object.keys(data).filter(key => key.includes('monitoring')));
        
        if (monitoringData) {
          try {
            const parsed = JSON.parse(monitoringData);
            console.log('Parsed monitoring data structure:', parsed);
            if (typeof parsed === 'object' && parsed !== null) {
              // New: free rows model (preferred)
              if (Array.isArray(parsed.rows)) {
                const ensureFive = (arr) => {
                  const makeEmpty = () => ({ objectives: '', performance_indicators: '', baseline_data: '', performance_target: '', data_source: '', collection_method: '', frequency: '', responsible: '' });
                  const copy = [...arr];
                  while (copy.length < 5) copy.push(makeEmpty());
                  return copy;
                };
                setMeRows(ensureFive(parsed.rows));
              }
              // Check what fields actually exist in the parsed data
              Object.keys(parsed).forEach(key => {
                console.log(`${key} fields:`, Object.keys(parsed[key] || {}));
              });
              
              // Ensure all required fields exist for each objective type
              Object.keys(meData).forEach(key => {
                if (parsed[key]) {
                  console.log(`Loading ${key} data:`, parsed[key]);
                  meData[key] = {
                    objectives: parsed[key].objectives || '',
                    performance_indicators: parsed[key].performance_indicators || '',
                    baseline_data: parsed[key].baseline_data || '',
                    performance_target: parsed[key].performance_target || '',
                    data_source: parsed[key].data_source || '',
                    collection_method: parsed[key].collection_method || '',
                    frequency: parsed[key].frequency || '',
                    responsible: parsed[key].responsible || ''
                  };
                  console.log(`Mapped ${key} data:`, meData[key]);
                  console.log(`Performance Indicators for ${key}:`, parsed[key].performance_indicators, '->', meData[key].performance_indicators);
                }
              });
            }
          } catch (e) {
            console.log('Could not parse monitoring_evaluation as JSON, using as text');
            meData.impact.objectives = monitoringData;
          }
        }
        

        // Parse fund source and extract "Others" text
        let fundSourceValue = '';
        let othersText = '';
        
        // Handle both array and string formats
        let parsedFundSource = data.fund_source;
        
        // If fund_source is a JSON string, parse it
        if (typeof data.fund_source === 'string' && data.fund_source.startsWith('[')) {
          try {
            parsedFundSource = JSON.parse(data.fund_source);
          } catch (e) {
            console.log('Failed to parse fund_source JSON:', e);
          }
        }
        
        if (Array.isArray(parsedFundSource)) {
          // If it's an array, take the first non-empty value
          fundSourceValue = parsedFundSource.find(fs => fs && fs.trim()) || '';
        } else if (typeof parsedFundSource === 'string') {
          fundSourceValue = parsedFundSource;
        }
        
        console.log('🔍 DEBUG - Loading project fund source:', {
          raw: data.fund_source,
          parsed: parsedFundSource,
          isArray: Array.isArray(parsedFundSource),
          extracted: fundSourceValue
        });
        
        if (fundSourceValue.startsWith('Others:')) {
          othersText = fundSourceValue.replace('Others:', '').trim();
          fundSourceValue = 'Others';
        }

        // Load user data for project leader
        let projectLeader = null;

        // Load project leader - prioritize project_leader_name from database
        if (data.project_leader_name) {
          // Parse the name to remove any task information
          const nameOnly = data.project_leader_name.split(' - ')[0];
          
          // First try to find the user by ID if available
        if (data.project_leader_id) {
          try {
            const { data: leaderData } = await axios.get(`/users/${data.project_leader_id}`);
            projectLeader = leaderData;
          } catch (e) {
              // If API fails, create user object from the name and ID
              projectLeader = { 
                user_id: data.project_leader_id, 
                fullname: nameOnly || 'Unknown User',
                role: '',
                department_college: ''
              };
            }
          } else {
            // No ID available, create a simple user object
            projectLeader = { 
              user_id: `temp_${Date.now()}`, 
              fullname: nameOnly || 'Unknown User',
              role: '',
              department_college: '',
              isTemporary: true
            };
          }
        }




        const formatTimeForInput = (timeString) => {
          if (!timeString) return '';
          if (timeString.match(/^\d{2}:\d{2}$/)) return timeString;
          if (timeString.match(/^\d{2}:\d{2}:\d{2}$/)) return timeString.substring(0, 5);
          return timeString;
        };

        
        // Map database values to form values
        const mapRequestType = (dbValue) => {
          if (dbValue === 'Client Requested') return 'Client Requested';
          if (dbValue === 'Department Initiative') return 'Department Initiative';
          return dbValue;
        };
        
        const mapInitiativeType = (dbValue) => {
          // Map database values to form values
          if (dbValue === 'Program') return 'Program';
          if (dbValue === 'Project') return 'Project';
          if (dbValue === 'Activity') return 'Activity';
          return dbValue || 'Project'; // Default to Project if null/undefined
        };
        
        // Parse extension agenda - might be stored as array or string
        let extensionAgenda = data.extension_agenda || '';
        if (Array.isArray(extensionAgenda)) {
          extensionAgenda = extensionAgenda[0] || '';
        }
        // Also check if it's a JSON string that needs parsing
        if (typeof extensionAgenda === 'string' && extensionAgenda.startsWith('[')) {
          try {
            const parsed = JSON.parse(extensionAgenda);
            extensionAgenda = parsed[0] || '';
          } catch (e) {
            // Could not parse extension agenda JSON
          }
        }
        
        const formData = {
          request_type: mapRequestType(data.request_type) || '',
          event_type: mapInitiativeType(data.event_type || data.initiative_type) || '',
          title: data.title || '',
          location: data.location || '',
          start_date: data.start_date ? (() => {
            const date = new Date(data.start_date);
            date.setDate(date.getDate() + 1);
            return date.toISOString().split('T')[0];
          })() : '',
          end_date: data.end_date ? (() => {
            const date = new Date(data.end_date);
            date.setDate(date.getDate() + 1);
            return date.toISOString().split('T')[0];
          })() : '',
          start_time: formatTimeForInput(data.start_time),
          end_time: formatTimeForInput(data.end_time),
          status: data.status || '',
          extension_agenda: extensionAgenda,
          sdg_goals: parseList(data.sdg_goals),
          offices_involved: data.offices_involved || '',
          programs_involved: data.programs_involved || '',
          project_leader: projectLeader,
          project_leader_task: data.project_leader_task || '',
          partner_agencies: data.partner_agencies || '',
          beneficiaries: data.beneficiaries || '',
          total_cost: data.total_cost ? formatNumberWithCommas(data.total_cost) : '',
          fund_source: fundSourceValue,
          rationale: data.rationale || '',
          objectives: data.objectives || '',
          expected_output: data.expected_output || '',
          strategies_methods: data.strategies_methods || '',
          financial_plan_details: data.financial_plan_details || '',
          functional_relationships: data.functional_relationships || '',
          monitoring_evaluation: data.monitoring_evaluation || '',
          sustainability_plan: data.sustainability_plan || ''
        };
        
        setForm(formData);
        console.log('Form Data Set:', formData);

        // Set the selected users for the UI
        setSelectedProjectLeader(projectLeader);
        
        // Add project leader to user search options if not already present
        if (projectLeader) {
          setUserSearchOptions(prev => {
            const exists = prev.find(u => u.user_id === projectLeader.user_id);
            if (!exists) {
              return [...prev, projectLeader];
            }
            return prev;
          });
        }

        // Update project leader task if it exists in the database
        if (data.project_leader_task) {
          setForm(prev => ({ 
            ...prev, 
            project_leader_task: data.project_leader_task
          }));
        }

        
        // Assistant Project Leaders - parse new separate structure
        const parsedAssistants = [];
        const parsedAssistantTasks = {};
        
        // Load names from assistant_project_leader_names (JSON array)
        let namesArray = [];
        if (data.assistant_project_leader_names) {
          try {
            const v = typeof data.assistant_project_leader_names === 'string' && data.assistant_project_leader_names.trim().startsWith('[')
              ? JSON.parse(data.assistant_project_leader_names)
              : data.assistant_project_leader_names;
            namesArray = Array.isArray(v) ? v : [];
          } catch {
            namesArray = [];
          }
        }
        
        // Load IDs from assistant_project_leader_ids (JSON array)
        let idsArray = [];
        if (data.assistant_project_leader_ids) {
          try {
            const v = typeof data.assistant_project_leader_ids === 'string' && data.assistant_project_leader_ids.trim().startsWith('[')
              ? JSON.parse(data.assistant_project_leader_ids)
              : data.assistant_project_leader_ids;
            idsArray = Array.isArray(v) ? v : [];
          } catch {
            idsArray = [];
          }
        }
        
        // Load tasks from assistant_project_leader_tasks (JSON object)
        let tasksObj = {};
        if (data.assistant_project_leader_tasks) {
          try {
            const v = typeof data.assistant_project_leader_tasks === 'string' && data.assistant_project_leader_tasks.trim().startsWith('{')
              ? JSON.parse(data.assistant_project_leader_tasks)
              : data.assistant_project_leader_tasks;
            tasksObj = typeof v === 'object' && v !== null ? v : {};
          } catch {
            tasksObj = {};
          }
        }
        
        // Create user objects with proper IDs and tasks
        namesArray.forEach((name, index) => {
          const user_id = idsArray[index] || Date.now() + Math.random();
          const userObj = { user_id, fullname: name, role: '', department_college: '' };
          parsedAssistants.push(userObj);
          // Try to find task by user_id first, then by index as fallback
          if (tasksObj[user_id]) {
            parsedAssistantTasks[user_id] = tasksObj[user_id];
          } else if (tasksObj[`assistant_${index}`]) {
            parsedAssistantTasks[user_id] = tasksObj[`assistant_${index}`];
          }
        });
        if (parsedAssistants.length > 0) {
          setAssistantLeaders(parsedAssistants);
          setAssistantTasks(parsedAssistantTasks);
          setUserSearchOptions(prev => {
            const existingIds = new Set(prev.map(u => u.user_id));
            return [...prev, ...parsedAssistants.filter(u => !existingIds.has(u.user_id))];
          });
        } else {
          // Ensure we always have at least one default field
          setAssistantLeaders([{ user_id: '', fullname: '', role: '', department_college: '' }]);
        }

        // Coordinators - parse new separate structure
        const parsedCoordinators = [];
        const parsedCoordinatorTasks = {};
        
        // Load names from coordinator_names (JSON array)
        let coordinatorNamesArray = [];
        if (data.coordinator_names) {
          try {
            const v = typeof data.coordinator_names === 'string' && data.coordinator_names.trim().startsWith('[')
              ? JSON.parse(data.coordinator_names)
              : data.coordinator_names;
            coordinatorNamesArray = Array.isArray(v) ? v : [];
          } catch {
            coordinatorNamesArray = [];
          }
        }
        
        // Load IDs from coordinator_ids (JSON array)
        let coordinatorIdsArray = [];
        if (data.coordinator_ids) {
          try {
            const v = typeof data.coordinator_ids === 'string' && data.coordinator_ids.trim().startsWith('[')
              ? JSON.parse(data.coordinator_ids)
              : data.coordinator_ids;
            coordinatorIdsArray = Array.isArray(v) ? v : [];
          } catch {
            coordinatorIdsArray = [];
          }
        }
        
        // Load tasks from coordinator_tasks (JSON object)
        let coordinatorTasksObj = {};
        if (data.coordinator_tasks) {
          try {
            const v = typeof data.coordinator_tasks === 'string' && data.coordinator_tasks.trim().startsWith('{')
              ? JSON.parse(data.coordinator_tasks)
              : data.coordinator_tasks;
            coordinatorTasksObj = typeof v === 'object' && v !== null ? v : {};
          } catch {
            coordinatorTasksObj = {};
          }
        }
        
        // Create user objects with proper IDs and tasks
        coordinatorNamesArray.forEach((name, index) => {
          const user_id = coordinatorIdsArray[index] || Date.now() + Math.random();
          const userObj = { user_id, fullname: name, role: '', department_college: '' };
          parsedCoordinators.push(userObj);
          // Try to find task by user_id first, then by index as fallback
          if (coordinatorTasksObj[user_id]) {
            parsedCoordinatorTasks[user_id] = coordinatorTasksObj[user_id];
          } else if (coordinatorTasksObj[`coordinator_${index}`]) {
            parsedCoordinatorTasks[user_id] = coordinatorTasksObj[`coordinator_${index}`];
          }
        });
        if (parsedCoordinators.length > 0) {
          // Only take the first coordinator for single selection
          setCoordinators([parsedCoordinators[0]]);
          setCoordinatorTasks(parsedCoordinatorTasks);
          setUserSearchOptions(prev => {
            const existingIds = new Set(prev.map(u => u.user_id));
            return [...prev, ...parsedCoordinators.filter(u => !existingIds.has(u.user_id))];
          });
        } else {
          // Ensure we always have at least one default field
          setCoordinators([{ user_id: '', fullname: '', role: '', department_college: '' }]);
        }

        // Project Staff - separate from coordinators
        const parsedStaff = [];
        const parsedStaffTasks = {};
        
        // Load names from project_staff_names (JSON array) - separate from coordinators
        let staffNamesArray = [];
        if (data.project_staff_names) {
          try {
            const v = typeof data.project_staff_names === 'string' && data.project_staff_names.trim().startsWith('[')
              ? JSON.parse(data.project_staff_names)
              : data.project_staff_names;
            staffNamesArray = Array.isArray(v) ? v : [];
          } catch {
            staffNamesArray = [];
          }
        }
        
        // Load IDs from project_staff_ids (JSON array)
        let staffIdsArray = [];
        if (data.project_staff_ids) {
          try {
            const v = typeof data.project_staff_ids === 'string' && data.project_staff_ids.trim().startsWith('[')
              ? JSON.parse(data.project_staff_ids)
              : data.project_staff_ids;
            staffIdsArray = Array.isArray(v) ? v : [];
          } catch {
            staffIdsArray = [];
          }
        }
        
        // Load tasks from project_staff_tasks (JSON object)
        let staffTasksObj = {};
        if (data.project_staff_tasks) {
          try {
            const v = typeof data.project_staff_tasks === 'string' && data.project_staff_tasks.trim().startsWith('{')
              ? JSON.parse(data.project_staff_tasks)
              : data.project_staff_tasks;
            staffTasksObj = typeof v === 'object' && v !== null ? v : {};
          } catch {
            staffTasksObj = {};
          }
        }
        
        // Create user objects with proper IDs and tasks
        staffNamesArray.forEach((name, index) => {
          const user_id = staffIdsArray[index] || Date.now() + Math.random();
          const userObj = { user_id, fullname: name, role: '', department_college: '' };
          parsedStaff.push(userObj);
          // Try to find task by user_id first, then by index as fallback
          if (staffTasksObj[user_id]) {
            parsedStaffTasks[user_id] = staffTasksObj[user_id];
          } else if (staffTasksObj[`staff_${index}`]) {
            parsedStaffTasks[user_id] = staffTasksObj[`staff_${index}`];
          }
        });
        if (parsedStaff.length > 0) {
          setProjectStaffs(parsedStaff);
          setProjectStaffTasks(parsedStaffTasks);
          setUserSearchOptions(prev => {
            const existingIds = new Set(prev.map(u => u.user_id));
            return [...prev, ...parsedStaff.filter(u => !existingIds.has(u.user_id))];
          });
        } else {
          setProjectStaffs([{ user_id: '', fullname: '', role: '', department_college: '' }]);
        }

        setFundSourceOther(othersText);
        console.log('About to set meTableData with:', meData);
        console.log('Performance Indicators in meData before setting:', {
          impact: meData.impact.performance_indicators,
          outcome: meData.outcome.performance_indicators,
          output: meData.output.performance_indicators,
          activities: meData.activities.performance_indicators,
          input: meData.input.performance_indicators
        });
        setMeTableData(meData);
        console.log('Monitoring Evaluation Data:', meData);
        console.log('Performance Indicators specifically:', {
          impact: meData.impact.performance_indicators,
          outcome: meData.outcome.performance_indicators,
          output: meData.output.performance_indicators,
          activities: meData.activities.performance_indicators,
          input: meData.input.performance_indicators
        });
        console.log('Raw monitoring_evaluation from DB:', data.monitoring_evaluation);
        console.log('Raw monitoring_evaluation_table from DB:', data.monitoring_evaluation_table);
        console.log('Parsed monitoring data:', monitoringData ? JSON.parse(monitoringData) : 'No data');
        console.log('Full project data keys:', Object.keys(data));

        // Parse participants if they exist
        if (data.participants) {
          try {
            const parsedParticipants = JSON.parse(data.participants);
            setParticipants(Array.isArray(parsedParticipants) ? parsedParticipants : []);
      } catch (e) {
            setParticipants([]);
          }
        }

      } catch (err) {
        setError('Failed to load project details');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);


  // Load eligible participants
  useEffect(() => {
    const loadEligible = async () => {
      try {
        const { data } = await axios.get('/users/eligible-participants');
        setParticipantsOptions(data);
        setUserSearchOptions(data); // Use same data for user search
        
        // If we have a temporary project leader, try to find them in the user system
        if (form.project_leader && form.project_leader.isTemporary) {
          const matchingUser = data.find(user => 
            user.fullname.toLowerCase() === form.project_leader.fullname.toLowerCase()
          );
          
          if (matchingUser) {
            // Found a match! Update the project leader to use the real user
            setForm(prev => ({
              ...prev,
              project_leader: matchingUser
            }));
            setSelectedProjectLeader(matchingUser);
          }
        }
      } catch (err) {
        // Non-blocking
        console.error('Failed to load participants', err);
      }
    };
    loadEligible();
  }, [form.project_leader]);

  // eslint-disable-next-line no-unused-vars
  const toggleParticipant = (userId) => {
    setParticipants((prev) => {
      const set = new Set(prev);
      if (set.has(userId)) set.delete(userId); else set.add(userId);
      return Array.from(set);
    });
  };

  // Toggle functions for each user field
  // eslint-disable-next-line no-unused-vars
  const toggleProjectLeader = (user) => {
    const isSameAsCurrent = selectedProjectLeader?.user_id === user.user_id;
    const nextSelected = isSameAsCurrent ? null : user;
    setSelectedProjectLeader(nextSelected);
    setForm(prev => ({ ...prev, project_leader: nextSelected }));
  };

  const addAssistantLeader = (user) => {
    setAssistantLeaders(prev => [...prev, user]);
  };
  const removeAssistantLeader = (userId) => {
    setAssistantLeaders(prev => prev.filter(u => u.user_id !== userId));
    setAssistantTasks(prev => {
      const next = { ...prev };
      delete next[userId];
      return next;
    });
  };

  // eslint-disable-next-line no-unused-vars
  const addCoordinator = (user) => {
    setCoordinators(prev => [...prev, user]);
  };
  // eslint-disable-next-line no-unused-vars
  const removeCoordinator = (userId) => {
    setCoordinators(prev => prev.filter(u => u.user_id !== userId));
    setCoordinatorTasks(prev => {
      const next = { ...prev };
      delete next[userId];
      return next;
    });
  };

  // Project Staff add/remove
  const addProjectStaff = (user) => {
    setProjectStaffs(prev => [...prev, user]);
  };
  const removeProjectStaff = (userId) => {
    setProjectStaffs(prev => prev.filter(u => u.user_id !== userId));
    setProjectStaffTasks(prev => {
      const next = { ...prev };
      delete next[userId];
      return next;
    });
  };



  // Filter users based on search term
  // eslint-disable-next-line no-unused-vars
  const getFilteredUsers = (searchTerm) => {
    // Handle null, undefined, or non-string values
    if (!searchTerm || typeof searchTerm !== 'string' || !searchTerm.trim()) {
      return [];
    }
    
    return userSearchOptions.filter(user => 
      user.fullname.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.role.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.department_college.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };






  const submit = async () => {
    setSaving(true);
    setError('');
    setShowConfirmModal(false);

    try {
        // Include dates in the main project update instead of separate endpoint
        console.log('Form date values before processing:', {
          start_date: form.start_date,
          end_date: form.end_date,
          start_time: form.start_time,
          end_time: form.end_time
        });

      // Prepare payload for all fields including dates
      const payload = { ...form };
      
      // Remove commas from total_cost before sending to database
      if (payload.total_cost) {
        payload.total_cost = removeCommas(payload.total_cost);
      }
      if (participants.length > 0) {
        payload.participants = participants;
      }
      
      // Convert extension_agenda string to array for backend compatibility
      if (form.extension_agenda) {
        payload.extension_agenda = [form.extension_agenda];
      }
      
      // Ensure fund_source is always an array for backend compatibility
      if (form.fund_source === 'Others' && fundSourceOther.trim()) {
        payload.fund_source = [`Others: ${fundSourceOther.trim()}`];
      } else if (form.fund_source && form.fund_source !== 'Others') {
        payload.fund_source = [form.fund_source];
      } else {
        payload.fund_source = [];
      }
      
      // Include monitoring and evaluation custom rows (new model)
      payload.monitoring_evaluation_table = { rows: meRows };

      // Do not send status if user cannot edit it
      if (!canEditStatus) {
        delete payload.status;
      }
      
      // Convert user objects to IDs for backend
      if (payload.project_leader) {
        // Handle temporary users (those not in the system)
        if (payload.project_leader.isTemporary) {
          // For temporary users, only send the name (no task in name)
          payload.project_leader_name = payload.project_leader.fullname;
          delete payload.project_leader_id; // Don't send fake ID
        } else {
          // For real users, send the ID and name separately
          payload.project_leader_id = payload.project_leader.user_id;
          payload.project_leader_name = payload.project_leader.fullname;
        }
        delete payload.project_leader;
        // Keep project_leader_task as separate field
      }
      
      // Handle assistant project leaders (multi) - filter out empty entries
      const validAssistantLeaders = assistantLeaders.filter(u => u.fullname && u.fullname.trim() !== '');
      let validAssistantTasks = {};
      if (validAssistantLeaders.length > 0) {
        payload.assistant_project_leader_ids = validAssistantLeaders.map(u => u.user_id);
        payload.assistant_project_leader_names = validAssistantLeaders.map(u => u.fullname);
        // Filter tasks to match only valid leaders using user_id as key
        validAssistantLeaders.forEach(leader => {
          if (assistantTasks[leader.user_id]) {
            validAssistantTasks[leader.user_id] = assistantTasks[leader.user_id];
          }
        });
        payload.assistant_project_leader_tasks = validAssistantTasks;
      } else {
        payload.assistant_project_leader_ids = [];
        payload.assistant_project_leader_names = [];
        payload.assistant_project_leader_tasks = {};
      }
      
      // Handle coordinator (single) - filter out empty entries
      const validCoordinator = coordinators[0] && coordinators[0].fullname && coordinators[0].fullname.trim() !== '' ? coordinators[0] : null;
      let validCoordinatorTasks = {};
      if (validCoordinator) {
        payload.coordinator_ids = [validCoordinator.user_id];
        payload.coordinator_names = [validCoordinator.fullname];
        // Filter tasks to match only valid coordinator using user_id as key
        if (coordinatorTasks[validCoordinator.user_id]) {
          validCoordinatorTasks[validCoordinator.user_id] = coordinatorTasks[validCoordinator.user_id];
        }
        payload.coordinator_tasks = validCoordinatorTasks;
      } else {
        payload.coordinator_ids = [];
        payload.coordinator_names = [];
        payload.coordinator_tasks = {};
      }
      
      // Handle project staff (multi) - separate from coordinators
      const validProjectStaffs = projectStaffs.filter(u => u.fullname && u.fullname.trim() !== '');
      let validProjectStaffTasks = {};
      if (validProjectStaffs.length > 0) {
        payload.project_staff_ids = validProjectStaffs.map(u => u.user_id);
        payload.project_staff_names = validProjectStaffs.map(u => u.fullname);
        // Filter tasks to match only valid project staff using user_id as key
        validProjectStaffs.forEach(staff => {
          if (projectStaffTasks[staff.user_id]) {
            validProjectStaffTasks[staff.user_id] = projectStaffTasks[staff.user_id];
          }
        });
        payload.project_staff_tasks = validProjectStaffTasks;
      } else {
        payload.project_staff_ids = [];
        payload.project_staff_names = [];
        payload.project_staff_tasks = {};
      }
      
      
      await axios.put(`/projects/${id}`, payload);
      showSuccess(NotificationMessages.PROJECT_UPDATED);
      
      // If STF or MDS was selected, redirect to Line-Item Budget form instead
      if (form.fund_source === 'STF' || form.fund_source === 'MDS') {
        // Wait a moment for the database update to complete
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Redirect to line-item budget form with the project ID
        navigate('/projects/budget', { state: { projectId: id } });
        return;
      }
      
      navigate('/projects');
    } catch (err) {
      try {
        const { data } = await axios.get(`/projects/${id}`);
        const leaderNameFromForm = form.project_leader ? (
          form.project_leader_task ? `${form.project_leader.fullname} - ${form.project_leader_task}` : form.project_leader.fullname
        ) : null;
        const leaderMatches = leaderNameFromForm && (data.project_leader_name === leaderNameFromForm || data.project_leader_name?.startsWith(form.project_leader.fullname));
        const basicMatches = (
          (data.title || '') === (form.title || '') ||
          (data.location || '') === (form.location || '')
        );
        if (leaderMatches || basicMatches) {
          showSuccess(NotificationMessages.PROJECT_UPDATED);
          navigate('/projects');
          return;
        }
      } catch (_) {
        // Ignore fallback failures and show original error below
      }
      setError(
        err.response?.data?.details ||
        err.response?.data?.error ||
        'Failed to update project. Please try again.'
      );
    } finally {
      setSaving(false);
    }
  };

  // Derived role options and filtered participants
  // eslint-disable-next-line no-unused-vars
  const roleOptions = ['All', ...Array.from(new Set(participantsOptions.map((u) => u.role)))];
  
  // Build selected ID sets for cross-field exclusion
  const selectedProjectLeaderId = selectedProjectLeader?.user_id || null;
  const selectedParticipantIds = participants; // already array of IDs

  // For participants dropdown, exclude users already chosen in other fields
  const participantsExcludedIds = new Set([
    ...(selectedProjectLeaderId ? [selectedProjectLeaderId] : []),
    ...selectedParticipantIds
  ]);

  // Filter participants based on role, search term, and exclusions
  // eslint-disable-next-line no-unused-vars
  const filteredParticipantsOptions = participantsOptions.filter((u) => {
    if (participantsExcludedIds.has(u.user_id)) return false;
    const roleMatch = roleFilter === 'All' || u.role === roleFilter;
    const searchMatch = !participantSearchTerm.trim() || 
      u.fullname.toLowerCase().includes(participantSearchTerm.toLowerCase()) ||
      u.role.toLowerCase().includes(participantSearchTerm.toLowerCase()) ||
      u.department_college.toLowerCase().includes(participantSearchTerm.toLowerCase());
    return roleMatch && searchMatch;
  });

  // Filter functions for each user field
  const getAvailableUsersForSelectionForField = (searchTerm, roleFilter, excludedIds = new Set()) => {
    if (!searchTerm || typeof searchTerm !== 'string' || !searchTerm.trim()) {
      return [];
    }
    
    return userSearchOptions.filter((u) => {
      if (excludedIds.has(u.user_id)) return false;
      const roleMatch = roleFilter === 'All' || u.role === roleFilter;
      const searchMatch = 
        u.fullname.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.role.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.department_college.toLowerCase().includes(searchTerm.toLowerCase());
      return roleMatch && searchMatch;
    });
  };

  // Build exclusion sets per field so selected users don't show in other fields
  const assistantIds = new Set(assistantLeaders.map(u => u.user_id));
  const coordinatorIds = new Set(coordinators.map(u => u.user_id));
  const projectStaffIds = new Set(projectStaffs.map(u => u.user_id));
  const excludeForProjectLeader = new Set([
    ...selectedParticipantIds,
    ...assistantIds,
    ...coordinatorIds,
    ...projectStaffIds,
  ]);
  // eslint-disable-next-line no-unused-vars
  const filteredProjectLeaderOptions = getAvailableUsersForSelectionForField(projectLeaderSearch, projectLeaderRoleFilter, excludeForProjectLeader);

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="container-fluid py-4">
      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-header">
              <h4 className="mb-0">Edit Project Proposal</h4>
            </div>
            <div className="card-body">
                {error && (
                  <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>
                )}
                <Form onSubmit={handleSubmitClick}>

            {/* Request Type - without title */}
            <Form.Group className="mb-3">
              <div>
                <Form.Check inline type="radio" name="request_type" id="request_type-client" label="Extension Service Program/Project/Activity is requested by clients." value="Client Requested" onChange={handleChange} onClick={handleChange} checked={form.request_type === 'Client Requested'} />
                <br />
                <Form.Check inline type="radio" name="request_type" id="request_type-department" label="Extension Service Program/Project/Activity is Department's initiative." value="Department Initiative" onChange={handleChange} onClick={handleChange} checked={form.request_type === 'Department Initiative'} />
              </div>
            </Form.Group>

            {/* Event Type - without title */}
            <Form.Group className="mb-3">
              <Row>
                <Col md={4}>
                  <Form.Check
                    type="radio"
                    id="event_type-program"
                    name="event_type"
                    value="Program"
                    checked={form.event_type === 'Program'}
                    onChange={handleChange}
                    onClick={handleChange}
                    label="Program"
                  />
                </Col>
                <Col md={4}>
                  <Form.Check
                    type="radio"
                    id="event_type-project"
                    name="event_type"
                    value="Project"
                    checked={form.event_type === 'Project'}
                    onChange={handleChange}
                    onClick={handleChange}
                    label="Project"
                  />
                </Col>
                <Col md={4}>
                  <Form.Check
                    type="radio"
                    id="event_type-activity"
                    name="event_type"
                    value="Activity"
                    checked={form.event_type === 'Activity'}
                    onChange={handleChange}
                    onClick={handleChange}
                    label="Activity"
                  />
                </Col>
              </Row>
            </Form.Group>

            <hr className="my-4" />

            <Row>
              <Col md={6}>
            <Form.Group className="mb-3">
                  <Form.Label><strong>Title:</strong></Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    name="title"
                    value={form.title}
                    onChange={handleChange}
                    placeholder="Enter project title"
                    maxLength={500}
                    style={{ border: '1px solid #888', fontSize: '1em', resize: 'vertical', whiteSpace: 'pre-wrap' }}
                  />
            </Form.Group>
              </Col>
              <Col md={6}>
            <Form.Group className="mb-3">
                  <Form.Label><strong>Location:</strong></Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    name="location"
                    value={form.location}
                    onChange={handleChange}
                    placeholder="Enter project location"
                    maxLength={500}
                    style={{ border: '1px solid #888', fontSize: '1em', resize: 'vertical', whiteSpace: 'pre-wrap' }}
                  />
            </Form.Group>
              </Col>
            </Row>

              <Row>
                <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label><strong>Start Date:</strong></Form.Label>
                  <Form.Control 
                    type="date" 
                    name="start_date" 
                    value={form.start_date}
                    onChange={handleChange}
                    disabled={!canEditDates}
                    style={{ border: '1px solid #888', fontSize: '1em' }}
                  />
                </Form.Group>
                </Col>
                <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label><strong>End Date:</strong></Form.Label>
                  <Form.Control 
                    type="date" 
                    name="end_date" 
                    value={form.end_date}
                    onChange={handleChange}
                    disabled={!canEditDates}
                    style={{ border: '1px solid #888', fontSize: '1em' }}
                  />
                </Form.Group>
                </Col>
              </Row>

            <Row>
                <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label><strong>Start Time (Optional):</strong></Form.Label>
                  <Form.Control 
                    type="time" 
                    name="start_time" 
                    value={form.start_time}
                    onChange={handleChange}
                    disabled={!canEditDates || form.event_type === 'Program'}
                    style={{ border: '1px solid #888', fontSize: '1em' }}
                  />
                </Form.Group>
                </Col>
                <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label><strong>End Time (Optional):</strong></Form.Label>
                  <Form.Control 
                    type="time" 
                    name="end_time" 
                    value={form.end_time}
                    onChange={handleChange}
                    disabled={!canEditDates || form.event_type === 'Program'}
                    style={{ border: '1px solid #888', fontSize: '1em' }}
                  />
                </Form.Group>
                </Col>
              </Row>

            {/* Extension Service Agenda - Section IV */}
            <hr />
            <h4 style={{ color: '#d32f2f' }}>Extension Service Agenda</h4>
            <Form.Group className="mb-3">
              {EXTENSION_AGENDAS.map((agenda) => (
                <Form.Check 
                  key={agenda} 
                  type="radio" 
                  label={agenda} 
                  name="extension_agenda"
                  value={agenda}
                  checked={form.extension_agenda === agenda} 
                  onChange={handleChange} 
                  onClick={handleChange}
                  className="mb-2"
                />
              ))}
            </Form.Group>

            {/* SDG Goals - Section V */}
            <hr />
            <h4 style={{ color: '#d32f2f' }}>Sustainable Development Goals (SDG)</h4>
            <Form.Group className="mb-3">
              <div style={{ display: 'flex', gap: '16px' }}>
                <div style={{ flex: 1 }}>
                  {SDG_GOALS.slice(0, 9).map((goal, index) => (
                    <Form.Check
                      key={index}
                      type="checkbox"
                      label={goal}
                      name="sdg_goals"
                      value={goal}
                      checked={form.sdg_goals.includes(goal)}
                      onChange={() => handleCheckboxGroup('sdg_goals', goal)}
                      onClick={() => handleCheckboxGroup('sdg_goals', goal)}
                      className="mb-2"
                    />
                  ))}
                </div>
                <div style={{ flex: 1 }}>
                  {SDG_GOALS.slice(9).map((goal, index) => (
                    <Form.Check
                      key={index}
                      type="checkbox"
                      label={goal}
                      name="sdg_goals"
                      value={goal}
                      checked={form.sdg_goals.includes(goal)}
                      onChange={() => handleCheckboxGroup('sdg_goals', goal)}
                      onClick={() => handleCheckboxGroup('sdg_goals', goal)}
                      className="mb-2"
                    />
                  ))}
                </div>
              </div>
            </Form.Group>

            {/* Offices/Colleges/Organizations Involved - Section VI */}
            <hr />
            <h4 style={{ color: '#d32f2f' }}>Offices/Colleges/Organizations Involved</h4>
            <Form.Group className="mb-3">
              <Form.Control
                as="textarea"
                rows={3}
                name="offices_involved"
                value={form.offices_involved}
                onChange={handleChange}
                placeholder="Enter offices/colleges/organizations involved"
                maxLength={500}
                style={{ border: '1px solid #888', fontSize: '1em' }}
              />
            </Form.Group>
            {/* Programs Involved - Section VII */}
            <hr />
            <h4 style={{ color: '#d32f2f' }}>Programs Involved</h4>
            <Form.Group className="mb-3">
              <Form.Control
                as="textarea"
                rows={3}
                name="programs_involved"
                value={form.programs_involved}
                onChange={handleChange}
                placeholder="Enter programs involved"
                maxLength={500}
                style={{ border: '1px solid #888', fontSize: '1em' }}
              />
            </Form.Group>



            {/* Project Leader, Assistant Project Leaders, Coordinators - Section VIII */}
            <hr />
            <h4 style={{ color: '#d32f2f' }}>Project Leadership</h4>
                    
            {/* Project Leader (single) */}
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label><strong>Project Leader:</strong></Form.Label>
                  <Form.Select
                    name="project_leader"
                    value={form.project_leader?.user_id || ''}
                    onChange={(e) => {
                      const userId = e.target.value;
                      if (userId) {
                        const user = userSearchOptions.find(u => u.user_id === parseInt(userId));
                        if (user) {
                          setSelectedProjectLeader(user);
                          setForm(prev => ({ ...prev, project_leader: user }));
                        }
                      } else {
                        setSelectedProjectLeader(null);
                        setForm(prev => ({ ...prev, project_leader: null }));
                      }
                    }}
                    style={{ border: '1px solid #888', fontSize: '1em' }}
                  >
                    <option value="">Select Project Leader</option>
                    {userSearchOptions
                      .filter(u => u.role !== 'Student')
                      .map(user => (
                        <option key={user.user_id} value={user.user_id}>
                          {user.role ? `${user.fullname} - ${user.role}` : user.fullname}
                        </option>
                      ))
                    }
                    {form.project_leader && form.project_leader.isTemporary && (
                      <option value={form.project_leader.user_id}>
                        {form.project_leader.fullname} (Current - Not in system)
                      </option>
                    )}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                      <Form.Control
                        as="textarea"
                    rows={3}
                    name="project_leader_task"
                    value={form.project_leader_task}
                    onChange={handleChange}
                        placeholder="Project Leader Task"
                        maxLength={2000}
                    style={{ border: '1px solid #888', fontSize: '1em', resize: 'vertical' }}
                  />
                </Form.Group>
              </Col>
            </Row>

            {/* Assistant Project Leaders (multiple) & Coordinators (multiple) */}
            <Row>
              <Col md={6}>
                
                <Form.Group className="mb-2 d-flex align-items-center justify-content-between">
                  <Form.Label className="mb-0"><strong>Assistant Project Leaders</strong></Form.Label>
                  <div>
                    <Button
                      variant="link"
                      className="p-0 me-2"
                      style={{ border: 'none', boxShadow: 'none', color: '#0d6efd' }}
                      title="Add assistant project leader"
                      aria-label="Add assistant project leader"
                      onClick={() => addAssistantLeader({ user_id: Date.now(), fullname: '', role: '', department_college: '' })}
                    >
                      <i className="bi bi-plus-circle-fill" style={{ fontSize: 18 }}></i>
                    </Button>
                    {assistantLeaders.length > 1 && (
                      <Button
                        variant="link"
                        className="p-0"
                        style={{ border: 'none', boxShadow: 'none', color: '#d32f2f' }}
                        title="Remove last assistant project leader"
                        aria-label="Remove assistant project leader"
                        onClick={() => removeAssistantLeader(assistantLeaders[assistantLeaders.length - 1]?.user_id)}
                      >
                        <i className="bi bi-dash-circle-fill" style={{ fontSize: 18 }}></i>
                      </Button>
                    )}
                  </div>
                </Form.Group>
                
                {/* Default Assistant Project Leader field - always shown */}
                <Row className="mb-2">
                      <Col md={6}>
                        <Form.Select
                      value={assistantLeaders[0]?.user_id || ''}
                          onChange={(e) => {
                            const userId = e.target.value;
                            if (userId) {
                              const user = userSearchOptions.find(u => u.user_id === parseInt(userId));
                              if (user) {
                                const updatedLeaders = [...assistantLeaders];
                            if (updatedLeaders.length === 0) {
                              updatedLeaders.push(user);
                            } else {
                              updatedLeaders[0] = user;
                            }
                                setAssistantLeaders(updatedLeaders);
                              }
                        } else {
                          // If no selection, ensure we have an empty placeholder
                          if (assistantLeaders.length === 0) {
                            setAssistantLeaders([{ user_id: '', fullname: '', role: '', department_college: '' }]);
                              }
                            }
                          }}
                          style={{ border: '1px solid #888', fontSize: '1em' }}
                        >
                          <option value="">Select Assistant Project Leader</option>
                          {userSearchOptions
                            .filter(u => u.role !== 'Student')
                        .filter(u => (!assistantLeaders.some(a => a.user_id === u.user_id) || u.user_id === assistantLeaders[0]?.user_id) && u.user_id !== parseInt(form.project_leader?.user_id) && !coordinators.some(c => c.user_id === u.user_id))
                            .map(user => (
                              <option key={user.user_id} value={user.user_id}>
                                {user.role ? `${user.fullname} - ${user.role}` : user.fullname}
                              </option>
                            ))
                          }
                        </Form.Select>
                      </Col>
                      <Col md={6}>
                      <Form.Control
                        as="textarea"
                        rows={2}
                          placeholder="Task"
                      value={assistantTasks[assistantLeaders[0]?.user_id] || ''}
                      onChange={(e) => setAssistantTasks(prev => ({ ...prev, [assistantLeaders[0]?.user_id]: e.target.value }))}
                          maxLength={2000}
                          style={{ border: '1px solid #888', fontSize: '1em', resize: 'vertical' }}
                      />
                      </Col>
                    </Row>
                
                {/* Additional Assistant Project Leader fields */}
                {assistantLeaders.slice(1).map((leader, idx) => (
                  <Row key={`apl-row-${idx + 1}`} className="mb-2">
                    <Col md={6}>
                        <Form.Select
                        value={leader.user_id || ''}
                        onChange={(e) => {
                          const userId = e.target.value;
                          if (userId) {
                          const user = userSearchOptions.find(u => u.user_id === parseInt(userId));
                          if (user) {
                              const updatedLeaders = [...assistantLeaders];
                              updatedLeaders[idx + 1] = user;
                              setAssistantLeaders(updatedLeaders);
                            }
                          }
                        }}
                        style={{ border: '1px solid #888', fontSize: '1em' }}
                      >
                        <option value="">Select Assistant Project Leader</option>
                        {userSearchOptions
                          .filter(u => u.role !== 'Student')
                          .filter(u => (!assistantLeaders.some(a => a.user_id === u.user_id) || u.user_id === leader.user_id) && u.user_id !== parseInt(form.project_leader?.user_id) && !coordinators.some(c => c.user_id === u.user_id))
                          .map(user => (
                            <option key={user.user_id} value={user.user_id}>
                              {user.role ? `${user.fullname} - ${user.role}` : user.fullname}
                            </option>
                          ))
                        }
                      </Form.Select>
                    </Col>
                    <Col md={6}>
                      <Form.Control
                        as="textarea"
                        rows={2}
                        placeholder="Task"
                        value={assistantTasks[leader.user_id] || ''}
                        onChange={(e) => setAssistantTasks(prev => ({ ...prev, [leader.user_id]: e.target.value }))}
                        maxLength={2000}
                        style={{ border: '1px solid #888', fontSize: '1em', resize: 'vertical' }}
                      />
                    </Col>
                  </Row>
                ))}
              </Col>

              <Col md={6}>
                
                <Form.Group className="mb-2">
                  <Form.Label className="mb-0"><strong>Coordinator</strong></Form.Label>
                </Form.Group>
                
                {/* Single Coordinator field */}
                <Row className="mb-2">
                  <Col md={6}>
                    <Form.Select
                      value={coordinators[0]?.user_id || ''}
                      onChange={(e) => {
                        const userId = e.target.value;
                        if (userId) {
                          const user = userSearchOptions.find(u => u.user_id === parseInt(userId));
                          if (user) {
                            setCoordinators([user]);
                          }
                        } else {
                          setCoordinators([{ user_id: '', fullname: '', role: '', department_college: '' }]);
                        }
                      }}
                      style={{ border: '1px solid #888', fontSize: '1em' }}
                    >
                      <option value="">Select Coordinator</option>
                      {userSearchOptions
                        .filter(u => u.role !== 'Student')
                        .filter(u => u.user_id !== parseInt(form.project_leader?.user_id) && !assistantLeaders.some(a => a.user_id === u.user_id))
                        .map(user => (
                          <option key={user.user_id} value={user.user_id}>
                            {user.role ? `${user.fullname} - ${user.role}` : user.fullname}
                          </option>
                        ))
                      }
                    </Form.Select>
                  </Col>
                  <Col md={6}>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      placeholder="Task"
                      value={coordinatorTasks[coordinators[0]?.user_id] || ''}
                      onChange={(e) => setCoordinatorTasks(prev => ({ ...prev, [coordinators[0]?.user_id]: e.target.value }))}
                      maxLength={2000}
                      style={{ border: '1px solid #888', fontSize: '1em', resize: 'vertical' }}
                    />
                  </Col>
                </Row>
              </Col>
            </Row>

            {/* Project Staff (multiple) */}
            <Row className="mt-3">
              <Col md={12}>
                <Form.Group className="mb-2 d-flex align-items-center justify-content-between">
                  <Form.Label className="mb-0"><strong>Project Staff</strong></Form.Label>
                  <div>
                    <Button
                      variant="link"
                      className="p-0 me-2"
                      style={{ border: 'none', boxShadow: 'none', color: '#0d6efd' }}
                      title="Add project staff"
                      aria-label="Add project staff"
                      onClick={() => addProjectStaff({ user_id: Date.now(), fullname: '', role: '', department_college: '' })}
                    >
                      <i className="bi bi-plus-circle-fill" style={{ fontSize: 18 }}></i>
                    </Button>
                    {projectStaffs.length > 1 && (
                      <Button
                        variant="link"
                        className="p-0"
                        style={{ border: 'none', boxShadow: 'none', color: '#d32f2f' }}
                        title="Remove last project staff"
                        aria-label="Remove project staff"
                        onClick={() => removeProjectStaff(projectStaffs[projectStaffs.length - 1]?.user_id)}
                      >
                        <i className="bi bi-dash-circle-fill" style={{ fontSize: 18 }}></i>
                      </Button>
                    )}
                  </div>
                </Form.Group>
              </Col>

              {projectStaffs.map((staff, idx) => (
                <Row key={`ps-row-${idx}`} className="mb-2">
                  <Col md={6}>
                    <Form.Select
                      value={staff.user_id || ''}
                      onChange={(e) => {
                        const userId = e.target.value;
                        if (userId) {
                          const user = userSearchOptions.find(u => u.user_id === parseInt(userId));
                          if (user) {
                            const updated = [...projectStaffs];
                            updated[idx] = user;
                            setProjectStaffs(updated);
                          }
                        }
                      }}
                      style={{ border: '1px solid #888', fontSize: '1em' }}
                    >
                      <option value="">Select Project Staff</option>
                      {userSearchOptions
                        .filter(u => u.role !== 'Student')
                        .filter(u => (
                          (!projectStaffs.some(s => s.user_id === u.user_id) || u.user_id === staff.user_id) &&
                          u.user_id !== parseInt(form.project_leader?.user_id) &&
                          !assistantLeaders.some(a => a.user_id === u.user_id) &&
                          !coordinators.some(c => c.user_id === u.user_id)
                        ))
                        .map(user => (
                          <option key={user.user_id} value={user.user_id}>
                            {user.role ? `${user.fullname} - ${user.role}` : user.fullname}
                          </option>
                        ))
                      }
                    </Form.Select>
                  </Col>
                  <Col md={6}>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      placeholder="Task"
                      value={projectStaffTasks[staff.user_id] || ''}
                      onChange={(e) => setProjectStaffTasks(prev => ({ ...prev, [staff.user_id]: e.target.value }))}
                      maxLength={2000}
                      style={{ border: '1px solid #888', fontSize: '1em', resize: 'vertical' }}
                    />
                  </Col>
                </Row>
              ))}
            </Row>

            {/* Partner Agencies - Section IX */}
            <hr />
            <h4 style={{ color: '#d32f2f' }}>Partner Agencies</h4>
            <Form.Group className="mb-3">
              <Form.Control
                as="textarea"
                rows={3}
                name="partner_agencies"
                value={form.partner_agencies}
                onChange={handleChange}
                placeholder="Enter partner agencies"
                maxLength={500}
                style={{ border: '1px solid #888', fontSize: '1em' }}
              />
            </Form.Group>

            {/* Beneficiaries - Section X */}
            <hr />
            <h4 style={{ color: '#d32f2f' }}>Beneficiaries</h4>
            <Form.Group className="mb-3">
              <Form.Control
                as="textarea"
                rows={3}
                name="beneficiaries"
                value={form.beneficiaries}
                onChange={handleChange}
                placeholder="Enter beneficiaries"
                maxLength={500}
                style={{ border: '1px solid #888', fontSize: '1em' }}
              />
            </Form.Group>

            {/* Total Cost - Section XI */}
            <hr />
            <h4 style={{ color: '#d32f2f' }}>Total Cost</h4>
            <Row>
              <Col md={6}>
            <Form.Group className="mb-3">
                  <Form.Label><strong>Total Cost:</strong></Form.Label>
                  <div className="input-group">
                    <span className="input-group-text">₱</span>
                  <Form.Control
                    type="text"
                    name="total_cost"
                    value={form.total_cost}
                    onChange={handleChange}
                    placeholder="Enter total cost (e.g., 1,000,000.50)"
                    maxLength={50}
                    style={{ border: '1px solid #888', fontSize: '1em' }}
                  />
                  </div>
            </Form.Group>
              </Col>
            </Row>

            {/* Fund Source - after Total Cost */}
            <hr />
            <h4 style={{ color: '#d32f2f' }}>Source of Fund</h4>
            <Form.Group className="mb-3">
              <Form.Label><strong>Source of fund:</strong></Form.Label>
              <div className="mb-2">
                {['STF', 'MDS', 'Others'].map((fs) => (
                  <Form.Check
                    key={fs}
                    inline
                    type="radio"
                    label={fs === 'Others' ? 'Others, (Please specify)' : fs}
                    name="fund_source"
                    value={fs}
                    checked={form.fund_source === fs}
                    onChange={handleChange}
                    onClick={handleChange}
                  />
                ))}
              </div>
              {form.fund_source === 'Others' && (
                <Form.Control
                  placeholder="Specify other source"
                  value={fundSourceOther}
                  onChange={handleFundSourceOther}
                  maxLength={255}
                  style={{ border: '1px solid #888', fontSize: '1em' }}
                />
              )}
            </Form.Group>

            {/* Long text sections */}
            {[
              ['rationale','Rationale (brief description of the situation)'],
                    ['objectives','Objectives (General and Specific)'],
              ['expected_output','Program/Project Expected Output'],
              ['strategies_methods','Description, Strategies and Methods (Activities / Schedule)'],
              // Financial plan moved to table below
                    ['functional_relationships','Functional Relationships with the Partner Agencies (Duties / Tasks of the Partner Agencies)']
            ].map(([name, label]) => (
              <Form.Group className="mb-3" key={name}>
                <Form.Label>{label}</Form.Label>
                      <Form.Control as="textarea" rows={name === 'strategies_methods' ? 4 : 3} name={name} value={form[name]} onChange={handleChange} />
              </Form.Group>
            ))}

            {/* Financial Plan Table */}
            <Form.Group className="mb-4">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <Form.Label className="h5 mb-0">Financial Plan</Form.Label>
                <div className="d-flex gap-2">
                  <Button variant="link" className="p-0" style={{ color: '#0d6efd' }} onClick={addFinancialPlanRow} title="Add row" aria-label="Add Financial Plan row">
                    <i className="bi bi-plus-circle-fill" style={{ fontSize: 18 }}></i>
                  </Button>
                  <Button variant="link" className="p-0" style={{ color: '#d32f2f' }} onClick={removeFinancialPlanRow} title="Remove last row" aria-label="Remove Financial Plan row" disabled={financialPlanRows.length <= 4}>
                    <i className="bi bi-dash-circle-fill" style={{ fontSize: 18 }}></i>
                  </Button>
                </div>
              </div>
              <div style={{ width: '100%', overflowX: 'auto', border: '1px solid #dee2e6' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', margin: 0 }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f8f9fa' }}>
                      <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'left', width: '50%' }}>Item Description</th>
                      <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', width: '10%' }}>Quantity</th>
                      <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', width: '10%' }}>Unit</th>
                      <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'right', width: '15%' }}>Price (Php)</th>
                      <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'right', width: '15%' }}>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {financialPlanRows.map((row, idx) => (
                      <tr key={`fp-row-${idx}`}>
                        <td style={{ border: '1px solid #000', padding: '6px' }}>
                          <input value={row.description} onChange={(e) => updateFinancialRow(idx, 'description', e.target.value)} style={{ width: '100%', border: 'none', outline: 'none' }} />
                        </td>
                        <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>
                          <input value={row.quantity} onChange={(e) => updateFinancialRow(idx, 'quantity', e.target.value)} style={{ width: '100%', border: 'none', outline: 'none', textAlign: 'center' }} />
                        </td>
                        <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>
                          <input value={row.unit} onChange={(e) => updateFinancialRow(idx, 'unit', e.target.value)} style={{ width: '100%', border: 'none', outline: 'none', textAlign: 'center' }} />
                        </td>
                        <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'right' }}>
                          <input value={row.price} onChange={(e) => updateFinancialRow(idx, 'price', e.target.value)} inputMode="decimal" pattern="[0-9]*" style={{ width: '100%', border: 'none', outline: 'none', textAlign: 'right' }} />
                        </td>
                        <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'right' }}>
                          <input value={row.total} onChange={(e) => updateFinancialRow(idx, 'total', e.target.value)} inputMode="decimal" pattern="[0-9]*" style={{ width: '100%', border: 'none', outline: 'none', textAlign: 'right' }} />
                        </td>
                      </tr>
                    ))}
                    <tr>
                      <td colSpan={4} style={{ border: '1px solid #000', padding: '6px', textAlign: 'right', fontWeight: 'bold', backgroundColor: '#f8f9fa' }}>TOTAL</td>
                      <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'right', fontWeight: 'bold' }}>{
                        (() => {
                          const g = getFinancialPlanGrandTotal();
                          return g ? g.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '';
                        })()
                      }</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </Form.Group>

            {/* Monitoring and Evaluation Table */}
            <Form.Group className="mb-3">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <Form.Label className="h5 mb-0">Monitoring and Evaluation Mechanics / Plan</Form.Label>
                <div className="d-flex gap-2">
                  <Button variant="link" className="p-0" style={{ color: '#0d6efd' }} onClick={addMeRow} title="Add row" aria-label="Add M&E row">
                    <i className="bi bi-plus-circle-fill" style={{ fontSize: 18 }}></i>
                  </Button>
                  <Button variant="link" className="p-0" style={{ color: '#d32f2f' }} onClick={removeMeRow} title="Remove last row" aria-label="Remove M&E row" disabled={meRows.length <= 5}>
                    <i className="bi bi-dash-circle-fill" style={{ fontSize: 18 }}></i>
                  </Button>
                </div>
              </div>
              <div style={{ maxHeight: '600px', overflowY: 'auto', overflowX: 'auto', border: '1px solid #dee2e6', width: '100%' }}>
                      <table style={{ width: '100%', marginBottom: 0, borderCollapse: 'collapse', tableLayout: 'fixed', minWidth: '1600px' }}>
                        <thead style={{ backgroundColor: '#e3f2fd', position: 'sticky', top: 0, zIndex: 1 }}>
                          <tr>
                            <th style={{ width: '15%', fontSize: '1rem', padding: '16px', border: '1px solid #dee2e6' }}>Objectives</th>
                            <th style={{ width: '20%', fontSize: '1rem', padding: '16px', border: '1px solid #dee2e6' }}>Performance Indicators</th>
                            <th style={{ width: '18%', fontSize: '1rem', padding: '16px', border: '1px solid #dee2e6' }}>Baseline Data</th>
                            <th style={{ width: '18%', fontSize: '1rem', padding: '16px', border: '1px solid #dee2e6' }}>Performance Target</th>
                            <th style={{ width: '18%', fontSize: '1rem', padding: '16px', border: '1px solid #dee2e6' }}>Data Source</th>
                            <th style={{ width: '18%', fontSize: '1rem', padding: '16px', border: '1px solid #dee2e6' }}>Collection Method</th>
                            <th style={{ width: '18%', fontSize: '1rem', padding: '16px', border: '1px solid #dee2e6' }}>Frequency of Data Collection</th>
                            <th style={{ width: '25%', fontSize: '1rem', padding: '16px', border: '1px solid #dee2e6' }}>Office/Persons Responsible</th>
                          </tr>
                      </thead>
                      <tbody>
                        {meRows.map((row, idx) => (
                          <tr key={`me-row-${idx}`} style={{ verticalAlign: 'top' }}>
                            <td style={{ padding: '12px', border: '1px solid #dee2e6' }}>
                              <textarea rows={4} value={row.objectives} onChange={(e) => setMeRows(prev => prev.map((r, i) => i === idx ? { ...r, objectives: e.target.value } : r))} style={{ fontSize: '1rem', resize: 'vertical', minHeight: '80px', border: '1px solid #ced4da', background: 'white', width: '100%', padding: '12px', margin: '0', outline: 'none' }} />
                            </td>
                            <td style={{ padding: '12px', border: '1px solid #dee2e6' }}>
                              <textarea rows={4} value={row.performance_indicators} onChange={(e) => setMeRows(prev => prev.map((r, i) => i === idx ? { ...r, performance_indicators: e.target.value } : r))} style={{ fontSize: '1rem', resize: 'vertical', minHeight: '80px', border: '1px solid #ced4da', background: 'white', width: '100%', padding: '12px', margin: '0', outline: 'none' }} />
                            </td>
                            <td style={{ padding: '12px', border: '1px solid #dee2e6' }}>
                              <textarea rows={4} value={row.baseline_data} onChange={(e) => setMeRows(prev => prev.map((r, i) => i === idx ? { ...r, baseline_data: e.target.value } : r))} style={{ fontSize: '1rem', resize: 'vertical', minHeight: '80px', border: '1px solid #ced4da', background: 'white', width: '100%', padding: '12px', margin: '0', outline: 'none' }} />
                            </td>
                            <td style={{ padding: '12px', border: '1px solid #dee2e6' }}>
                              <textarea rows={4} value={row.performance_target} onChange={(e) => setMeRows(prev => prev.map((r, i) => i === idx ? { ...r, performance_target: e.target.value } : r))} style={{ fontSize: '1rem', resize: 'vertical', minHeight: '80px', border: '1px solid #ced4da', background: 'white', width: '100%', padding: '12px', margin: '0', outline: 'none' }} />
                            </td>
                            <td style={{ padding: '12px', border: '1px solid #dee2e6' }}>
                              <textarea rows={4} value={row.data_source} onChange={(e) => setMeRows(prev => prev.map((r, i) => i === idx ? { ...r, data_source: e.target.value } : r))} style={{ fontSize: '1rem', resize: 'vertical', minHeight: '80px', border: '1px solid #ced4da', background: 'white', width: '100%', padding: '12px', margin: '0', outline: 'none' }} />
                            </td>
                            <td style={{ padding: '12px', border: '1px solid #dee2e6' }}>
                              <textarea rows={4} value={row.collection_method} onChange={(e) => setMeRows(prev => prev.map((r, i) => i === idx ? { ...r, collection_method: e.target.value } : r))} style={{ fontSize: '1rem', resize: 'vertical', minHeight: '80px', border: '1px solid #ced4da', background: 'white', width: '100%', padding: '12px', margin: '0', outline: 'none' }} />
                            </td>
                            <td style={{ padding: '12px', border: '1px solid #dee2e6' }}>
                              <textarea rows={4} value={row.frequency} onChange={(e) => setMeRows(prev => prev.map((r, i) => i === idx ? { ...r, frequency: e.target.value } : r))} style={{ fontSize: '1rem', resize: 'vertical', minHeight: '80px', border: '1px solid #ced4da', background: 'white', width: '100%', padding: '12px', margin: '0', outline: 'none' }} />
                            </td>
                            <td style={{ padding: '12px', border: '1px solid #dee2e6' }}>
                              <textarea rows={4} value={row.responsible} onChange={(e) => setMeRows(prev => prev.map((r, i) => i === idx ? { ...r, responsible: e.target.value } : r))} style={{ fontSize: '1rem', resize: 'vertical', minHeight: '80px', border: '1px solid #ced4da', background: 'white', width: '100%', padding: '12px', margin: '0', outline: 'none' }} />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Form.Group>

                  

                  {/* Sustainability Plan */}
                  <Form.Group className="mb-3">
                    <Form.Label>Sustainability Plan</Form.Label>
                    <Form.Control as="textarea" rows={3} name="sustainability_plan" value={form.sustainability_plan} onChange={handleChange} />
                  </Form.Group>

                  {/* Status Field - Admin Only */}
                  {canEditStatus && (
                    <Form.Group className="mb-3">
                      <Form.Label><strong>Project Status:</strong></Form.Label>
                      <Form.Select
                        name="status"
                        value={form.status}
                        onChange={handleChange}
                        style={{ border: '1px solid #888', fontSize: '1em' }}
                      >
                        <option value="Pending">Pending</option>
                        <option value="Reviewed by GAD">Reviewed by GAD</option>
                        <option value="Approved by Extension Head">Approved by Extension Head</option>
                        <option value="Rejected by Extension Head">Rejected by Extension Head</option>
                        <option value="Approved by VC-RDES">Approved by VC-RDES</option>
                        <option value="Rejected by VC-RDES">Rejected by VC-RDES</option>
                        <option value="Approved by VC-AF">Approved by VC-AF</option>
                        <option value="Rejected by VC-AF">Rejected by VC-AF</option>
                        <option value="Approved by Chancellor">Approved by Chancellor</option>
                        <option value="Rejected by Chancellor">Rejected by Chancellor</option>
                        <option value="Rejected by Admin">Rejected by Admin</option>
                        <option value="On-Going">On-Going</option>
                        <option value="Completed">Completed</option>
                      </Form.Select>
                      <small className="text-muted">Only Admin can change project status</small>
                    </Form.Group>
                  )}

            <div className="d-flex justify-content-end gap-2">
                    <Button variant="secondary" as={Link} to="/projects">Cancel</Button>
              <Button type="submit" variant="primary" disabled={loading}>{loading ? 'Saving...' : 'Save Changes'}</Button>
            </div>
          </Form>
        </div>
      </div>
    </div>
  </div>

      {/* Submit Confirmation Modal */}
      <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Project Update</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-center py-3">
            <i className="bi bi-check-circle-fill text-success" style={{ fontSize: '3rem' }}></i>
            <h5 className="mt-3 mb-2">Confirm Project Update</h5>
            <p className="text-muted">Are you sure you want to save these changes?</p>
            {form.fund_source?.trim() === 'STF' || form.fund_source?.trim() === 'MDS' ? (
              <div className="alert alert-info">
                <strong>Note:</strong> Since you selected STF or MDS as fund source, you will be redirected to the Line-Item Budget form after saving.
              </div>
            ) : null}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConfirmModal(false)}>Cancel</Button>
          <Button variant="success" onClick={submit} disabled={loading}>
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ProjectEdit;


